import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseConfig';
import './PasswordResetPage.css';

const PasswordResetPage = () => {
  const location = useLocation();

  // Store token in ref to prevent re-extraction
  const tokenRef = useRef(null);
  if (tokenRef.current === null) {
    const urlParams = new URLSearchParams(location.search);
    tokenRef.current = urlParams.get('token') || '';
  }

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [status, setStatus] = useState(() => tokenRef.current ? 'ready' : 'error');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState(() => tokenRef.current ? '' : 'No password reset token provided');

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handlePasswordReset = useCallback(async (e) => {
    e.preventDefault();

    if (!formData.newPassword || !formData.confirmPassword) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (formData.newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    setStatus('resetting');
    setErrorMessage('');

    try {
      // Call the Firebase function to verify token and reset password
      const verifyPasswordResetToken = httpsCallable(functions, 'verifyPasswordResetToken');
      const result = await verifyPasswordResetToken({
        token: tokenRef.current,
        newPassword: formData.newPassword
      });

      if (result.data.success) {
        setStatus('success');
        setEmail(result.data.email);
      } else {
        setStatus('error');
        setErrorMessage('Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      setStatus('error');

      // Handle different error types
      switch (error.code) {
        case 'functions/not-found':
          setErrorMessage('Invalid or expired reset link');
          break;
        case 'functions/already-exists':
          setErrorMessage('This reset link has already been used');
          break;
        case 'functions/deadline-exceeded':
          setErrorMessage('Password reset link has expired (links expire after 1 hour)');
          break;
        case 'functions/invalid-argument':
          setErrorMessage(error.message || 'Invalid password reset request');
          break;
        case 'functions/unauthenticated':
          setErrorMessage('Authentication error - please request a new reset link');
          break;
        case 'functions/unavailable':
          setErrorMessage('Service temporarily unavailable - please try again later');
          break;
        default:
          setErrorMessage(`Error: ${error.message || 'An error occurred while resetting your password. Please try again.'}`);
      }
    }
  }, [formData]);

  const ReadyState = useMemo(() => (
    <div className="reset-container">
      <div className="reset-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="reset-content">
          <h2>Reset Your Password</h2>
          <p>Enter your new password below.</p>

          <form onSubmit={handlePasswordReset} className="reset-form">
            {/* Hidden username field for accessibility */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              style={{ display: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            />

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="error-message" style={{ visibility: errorMessage ? 'visible' : 'hidden', minHeight: '20px' }}>
              {errorMessage || '\u00A0'}
            </div>

            <button type="submit" className="reset-button">
              Reset Password
            </button>
          </form>

          <div className="password-requirements">
            <p className="small-text">Password must be at least 6 characters long</p>
          </div>
        </div>
      </div>
    </div>
  ), [formData, errorMessage, handleInputChange, handlePasswordReset]);

  const ResettingState = () => (
    <div className="reset-container">
      <div className="reset-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="reset-content">
          <div className="spinner"></div>
          <h2>Resetting your password...</h2>
          <p>Please wait while we update your password.</p>
        </div>
      </div>
    </div>
  );

  const SuccessState = () => (
    <div className="reset-container">
      <div className="reset-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="reset-content">
          <div className="success-icon">✓</div>
          <h2>Password Reset Successfully!</h2>
          <p>Your password for <strong>{email}</strong> has been updated.</p>
          <p>You can now sign in to NextLink with your new password.</p>

          <div className="download-section">
            <h3>Download NextLink</h3>
            <div className="download-buttons">
              <a
                href="https://apps.apple.com/us/app/the-nextlink/id6743041580"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="app-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </a>
            </div>
          </div>

          <div className="next-steps">
            <p className="small-text">
              Open the NextLink app and sign in with your email and new password.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const ErrorState = () => (
    <div className="reset-container">
      <div className="reset-card">
        <div className="nextlink-header">
          <h1>NextLink</h1>
          <p className="tagline">Organize Smarter. Securely.</p>
        </div>

        <div className="reset-content">
          <div className="error-icon">✗</div>
          <h2>Password Reset Failed</h2>
          <p className="error-message">{errorMessage}</p>

          <div className="error-actions">
            <p>If you're having trouble, please:</p>
            <ul>
              <li>Check that you clicked the correct link from your email</li>
              <li>Make sure the reset link hasn't expired (links expire after 1 hour)</li>
              <li>Request a new password reset link in the NextLink app</li>
            </ul>
          </div>

          <div className="download-section">
            <h3>Download NextLink</h3>
            <div className="download-buttons">
              <a
                href="https://apps.apple.com/us/app/the-nextlink/id6743041580"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="app-store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink"
                className="store-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on current status
  switch (status) {
    case 'ready':
      return ReadyState;
    case 'resetting':
      return <ResettingState />;
    case 'success':
      return <SuccessState />;
    case 'error':
      return <ErrorState />;
    default:
      return ReadyState;
  }
};

export default PasswordResetPage;
